import React, { useState, useEffect } from 'react';
import { supabase } from '../../utils/supabase';
import { SettingsPageProps } from './types';
import './styles.scss';

const SettingsPage: React.FC<SettingsPageProps> = ({ user }) => {
  const [displayName, setDisplayName] = useState('');
  const [originalName, setOriginalName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [nameLoading, setNameLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  };

  useEffect(() => {
    if (user) {
      const name = user.user_metadata?.display_name || user.user_metadata?.full_name || '';
      setDisplayName(name);
      setOriginalName(name);
    }
  }, [user]);

  const handleUpdateName = async () => {
    if (!user) return;
    if (displayName.trim() === originalName) {
      showToast('No changes to save', 'info');
      return;
    }

    setNameLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        data: { display_name: displayName.trim(), full_name: displayName.trim() }
      });
      if (error) throw error;
      setOriginalName(displayName.trim());
      showToast('Name updated successfully!', 'success');
    } catch (err) {
      showToast(`Failed to update name: ${(err as Error).message}`, 'error');
    } finally {
      setNameLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword || !confirmPassword) {
      showToast('Please fill in both password fields', 'error');
      return;
    }
    if (newPassword.length < 6) {
      showToast('Password must be at least 6 characters', 'error');
      return;
    }
    if (newPassword !== confirmPassword) {
      showToast('Passwords do not match', 'error');
      return;
    }

    setPasswordLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      setNewPassword('');
      setConfirmPassword('');
      showToast('Password updated successfully!', 'success');
    } catch (err) {
      showToast(`Failed to change password: ${(err as Error).message}`, 'error');
    } finally {
      setPasswordLoading(false);
    }
  };

  if (!user) return null;

  const memberSince = user.created_at
    ? new Date(user.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : 'Unknown';

  return (
    <div className="page-container">
      {/* Toast Notification */}
      {toast && (
        <div className={`billing-toast billing-toast-${toast.type}`} onClick={() => setToast(null)}>
          <span className="billing-toast-icon">
            {toast.type === 'success' ? '✅' : toast.type === 'error' ? '❌' : 'ℹ️'}
          </span>
          <span className="billing-toast-message">{toast.message}</span>
          <button className="billing-toast-close" onClick={() => setToast(null)}>×</button>
        </div>
      )}

      <div className="page-header">
        <h1>Settings</h1>
        <p>Manage your account and preferences</p>
      </div>

      {/* Profile Section */}
      <div className="settings-section">
        <h2>Profile</h2>
        <div className="settings-card">
          <div className="profile-avatar-large">
            {(displayName || user.email || '?').charAt(0).toUpperCase()}
          </div>

          <div className="settings-form">
            <div className="form-group">
              <label className="form-label">Display Name</label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Enter your name"
                className="form-input settings-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                type="email"
                value={user.email || ''}
                className="form-input settings-input"
                disabled
              />
              <p className="form-hint">Email cannot be changed</p>
            </div>

            <div className="form-group">
              <label className="form-label">Member Since</label>
              <p className="form-value">{memberSince}</p>
            </div>

            <div className="form-group">
              <label className="form-label">User ID</label>
              <p className="form-value form-mono">{user.id}</p>
            </div>

            <button
              onClick={handleUpdateName}
              disabled={nameLoading || displayName.trim() === originalName}
              className="btn btn-primary"
            >
              {nameLoading ? 'Saving...' : 'Save Name'}
            </button>
          </div>
        </div>
      </div>

      {/* Change Password Section */}
      <div className="settings-section">
        <h2>Change Password</h2>
        <div className="settings-card">
          <div className="settings-form">
            <div className="form-group">
              <label className="form-label">New Password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Min 6 characters"
                className="form-input settings-input"
                minLength={6}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Confirm New Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repeat password"
                className="form-input settings-input"
              />
            </div>

            <button
              onClick={handleChangePassword}
              disabled={passwordLoading || !newPassword || !confirmPassword}
              className="btn btn-primary"
            >
              {passwordLoading ? 'Updating...' : 'Update Password'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
